// Equity offers page - View and manage equity offers
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase-auth'
import { useUser, usePermissions } from '@/lib/hooks'
import { useProject } from '@/contexts/project-context'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import EquityOfferForm from '@/components/custom-ui/equity-offer-form'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { 
  Gift,
  Clock,
  CheckCircle,
  XCircle,
  TrendingUp,
  Calendar,
  FileText,
  AlertCircle,
  Plus,
  Calculator,
  Info
} from 'lucide-react'
import { format } from 'date-fns'
import { Database } from '@/database.types'
import { toast } from 'sonner'

type EquityOffer = Database['public']['Tables']['equity_offers']['Row'] & {
  vesting_schedules: Database['public']['Tables']['vesting_schedules']['Row'] | null
  projects: Database['public']['Tables']['projects']['Row'] | null
  equity_classes: Database['public']['Tables']['equity_classes']['Row'] | null
  users?: Database['public']['Tables']['users']['Row'] | null
}

export default function EquityOffersPage() {
  const [selectedOffer, setSelectedOffer] = useState<EquityOffer | null>(null)
  const [actionType, setActionType] = useState<'accept' | 'reject' | null>(null)
  const [showCreateOfferDialog, setShowCreateOfferDialog] = useState(false)
  const queryClient = useQueryClient()
  const { profile } = useUser()
  const { currentProject } = useProject()
  const permissions = usePermissions()

  // Fetch equity offers - all for admins/owners, personal for regular users
  const { data: offers, isLoading } = useQuery({
    queryKey: ['equity-offers', profile?.id, currentProject?.id, permissions.canMakeEquityOffers],
    queryFn: async () => {
      if (!profile?.id) return []
      
      const query = supabase
        .from('equity_offers')
        .select(`
          *,
          vesting_schedules (*),
          projects (*),
          equity_classes (*),
          users!equity_offers_user_id_fkey (
            id,
            email,
            first_name,
            last_name
          )
        `)
        .order('created_at', { ascending: false })

      // If not admin/owner, only show user's own offers
      if (!permissions.canMakeEquityOffers) {
        query.eq('user_id', profile.id)
      }

      // If we have a current project, filter by it
      if (currentProject) {
        query.eq('project_id', currentProject.id)
      }

      const { data, error } = await query

      if (error) throw error
      return data as any[]
    },
    enabled: !!profile?.id
  })

  // Accept/Reject offer mutation
  const { mutate: updateOffer, isPending: isUpdating } = useMutation({
    mutationFn: async ({ offerId, status }: { offerId: string; status: 'accepted' | 'rejected' }) => {
      const { error } = await supabase
        .from('equity_offers')
        .update({ 
          status,
          updated_at: new Date().toISOString()
        })
        .eq('id', offerId)

      if (error) throw error

      // If accepted, create cap table entry
      if (status === 'accepted' && selectedOffer) {
        const { error: capTableError } = await supabase
          .from('cap_table_entries')
          .insert({
            user_id: profile!.id,
            percentage: selectedOffer.proposed_equity_percentage,
            shares: selectedOffer.shares_offered || 0, // Use shares from offer or 0 as default
            equity_class_id: selectedOffer.equity_class_id || '', // Default empty string if not specified
            project_id: selectedOffer.project_id
          })

        if (capTableError) throw capTableError
      }
    },
    onSuccess: (_, { status }) => {
      queryClient.invalidateQueries({ queryKey: ['equity-offers'] })
      queryClient.invalidateQueries({ queryKey: ['cap-table-entries'] })
      toast.success(`Offer ${status === 'accepted' ? 'accepted' : 'rejected'} successfully`)
      setSelectedOffer(null)
      setActionType(null)
    },
    onError: (error: any) => {
      toast.error(`Failed to update offer: ${error.message}`)
    }
  })

  const handleAction = (offer: EquityOffer, action: 'accept' | 'reject') => {
    setSelectedOffer(offer)
    setActionType(action)
  }

  const confirmAction = () => {
    if (selectedOffer && actionType) {
      updateOffer({ offerId: selectedOffer.id, status: actionType === 'accept' ? 'accepted' : 'rejected' })
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted':
        return <CheckCircle className="h-5 w-5 text-green-600" />
      case 'rejected':
        return <XCircle className="h-5 w-5 text-red-600" />
      case 'expired':
        return <Clock className="h-5 w-5 text-gray-400" />
      default:
        return <Clock className="h-5 w-5 text-yellow-600" />
    }
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
      pending: 'default',
      accepted: 'secondary',
      rejected: 'destructive',
      expired: 'outline'
    }

    return (
      <Badge variant={variants[status] || 'default'}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    )
  }

  const pendingOffers = offers?.filter(o => o.status === 'pending') || []
  const historicalOffers = offers?.filter(o => o.status !== 'pending') || []

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Equity Offers</h1>
          <p className="mt-1 text-sm text-gray-600">
            {permissions.canMakeEquityOffers 
              ? 'Create and manage equity offers for team members'
              : 'Review and manage your equity offers'}
          </p>
        </div>
        {permissions.canMakeEquityOffers && (
          <Button onClick={() => setShowCreateOfferDialog(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Equity Offer
          </Button>
        )}
      </div>

      {/* Pending Offers */}
      {pendingOffers.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900 flex items-center">
              <AlertCircle className="h-5 w-5 mr-2 text-yellow-600" />
              Pending Offers ({pendingOffers.length})
            </h2>
          </div>
          <div className="divide-y divide-gray-200">
            {pendingOffers.map((offer) => (
              <div key={offer.id} className="px-6 py-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <Gift className="h-5 w-5 mr-2 text-purple-600" />
                      <h3 className="text-lg font-medium text-gray-900">
                        {offer.shares_offered ? `${offer.shares_offered.toLocaleString()} shares` : `${offer.proposed_equity_percentage}%`} {offer.equity_classes?.class_name || 'Common'} Stock
                      </h3>
                      {offer.projects && (
                        <span className="ml-3 text-sm text-gray-500">
                          in {offer.projects.name}
                        </span>
                      )}
                    </div>

                    {permissions.canMakeEquityOffers && offer.users && (
                      <div className="mb-2 text-sm text-gray-600">
                        <strong>Recipient:</strong> {offer.users.first_name} {offer.users.last_name} ({offer.users.email})
                      </div>
                    )}

                    {offer.vesting_schedules && (
                      <div className="flex items-center space-x-4 mb-3 text-sm text-gray-600">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          <span>{offer.vesting_schedules.duration_months} months vesting</span>
                        </div>
                        <div className="flex items-center">
                          <TrendingUp className="h-4 w-4 mr-1" />
                          <span>{offer.vesting_schedules.cliff_months} months cliff</span>
                        </div>
                      </div>
                    )}

                    {/* Show contribution-based recommendation if available */}
                    {offer.recommended_shares && offer.shares_offered && (
                      <div className="mb-3 p-3 bg-blue-50 rounded-md border border-blue-200">
                        <div className="flex items-start space-x-2">
                          <Calculator className="h-4 w-4 text-blue-600 mt-0.5" />
                          <div className="text-sm">
                            <p className="font-medium text-blue-900">Contribution Analysis</p>
                            <p className="text-blue-700">
                              Recommended: {offer.recommended_shares.toLocaleString()} shares
                              {offer.shares_offered > offer.recommended_shares * 1.5 && (
                                <span className="ml-2 text-orange-600">
                                  (offer is {Math.round((offer.shares_offered / offer.recommended_shares - 1) * 100)}% above recommendation)
                                </span>
                              )}
                              {offer.shares_offered < offer.recommended_shares * 0.5 && (
                                <span className="ml-2 text-orange-600">
                                  (offer is {Math.round((1 - offer.shares_offered / offer.recommended_shares) * 100)}% below recommendation)
                                </span>
                              )}
                            </p>
                            {offer.contribution_snapshot && (
                              <p className="text-xs text-blue-600 mt-1">
                                Based on rank #{offer.contribution_snapshot.rank} with {offer.contribution_snapshot.task_count} contributions
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {offer.explanation && (
                      <div className="bg-gray-50 rounded-md p-3 mb-3">
                        <div className="flex items-start">
                          <FileText className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-700">{offer.explanation}</p>
                        </div>
                      </div>
                    )}

                    <p className="text-sm text-gray-500">
                      Offered on {format(new Date(offer.offer_date), 'MMMM d, yyyy')}
                    </p>
                  </div>

                  {(!permissions.canMakeEquityOffers || offer.user_id === profile?.id) && (
                    <div className="ml-4 flex flex-col space-y-2">
                      <Button
                        onClick={() => handleAction(offer, 'accept')}
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        Accept Offer
                      </Button>
                      <Button
                        onClick={() => handleAction(offer, 'reject')}
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50 border-red-300"
                      >
                        Reject Offer
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Pending Offers */}
      {pendingOffers.length === 0 && !isLoading && (
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <Gift className="h-12 w-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No Pending Offers</h3>
          <p className="text-sm text-gray-500">
            You don't have any pending equity offers at this time.
          </p>
        </div>
      )}

      {/* Historical Offers */}
      {historicalOffers.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Offer History</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {permissions.canMakeEquityOffers && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Recipient
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Shares
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Class
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Vesting
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {historicalOffers.map((offer) => (
                  <tr key={offer.id}>
                    {permissions.canMakeEquityOffers && (
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {offer.users ? `${offer.users.first_name} ${offer.users.last_name}` : 'Unknown'}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {offer.projects?.name || 'Unknown Project'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {offer.shares_offered ? offer.shares_offered.toLocaleString() : `${offer.proposed_equity_percentage}%`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <Badge variant="secondary">
                        {offer.equity_classes?.class_name || 'Common'}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {offer.vesting_schedules ? (
                        <span>
                          {offer.vesting_schedules.duration_months} months, 
                          {offer.vesting_schedules.cliff_months} month cliff
                        </span>
                      ) : (
                        'No vesting'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(new Date(offer.offer_date), 'MMM d, yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(offer.status)}
                        <span className="ml-2">{getStatusBadge(offer.status)}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <Dialog 
        open={!!selectedOffer && !!actionType} 
        onOpenChange={(open) => {
          if (!open) {
            setSelectedOffer(null)
            setActionType(null)
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionType === 'accept' ? 'Accept Equity Offer' : 'Reject Equity Offer'}
            </DialogTitle>
            <DialogDescription>
              {actionType === 'accept' 
                ? 'Are you sure you want to accept this equity offer? This will grant you equity in the project with the specified vesting terms.'
                : 'Are you sure you want to reject this equity offer? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOffer && (
            <div className="space-y-3 py-4">
              <div className="bg-gray-50 rounded-md p-4 space-y-2">
                <p className="text-sm">
                  <strong>Equity:</strong> {selectedOffer.proposed_equity_percentage}% {selectedOffer.equity_classes?.class_name || 'Common'} Stock
                </p>
                {selectedOffer.projects && (
                  <p className="text-sm">
                    <strong>Project:</strong> {selectedOffer.projects.name}
                  </p>
                )}
                {selectedOffer.vesting_schedules && (
                  <>
                    <p className="text-sm">
                      <strong>Vesting Period:</strong> {selectedOffer.vesting_schedules.duration_months} months
                    </p>
                    <p className="text-sm">
                      <strong>Cliff:</strong> {selectedOffer.vesting_schedules.cliff_months} months
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSelectedOffer(null)
                setActionType(null)
              }}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button 
              onClick={confirmAction}
              disabled={isUpdating}
              className={actionType === 'accept' ? 'bg-green-600 hover:bg-green-700' : ''}
              variant={actionType === 'reject' ? 'destructive' : 'default'}
            >
              {isUpdating ? 'Processing...' : `${actionType === 'accept' ? 'Accept' : 'Reject'} Offer`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Offer Dialog */}
      <EquityOfferForm
        open={showCreateOfferDialog}
        onOpenChange={setShowCreateOfferDialog}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['equity-offers'] })
          setShowCreateOfferDialog(false)
        }}
      />
    </div>
  )
}